import React from "react";

// ============================================
// CSS Animations (injected into document.head)
// ============================================

const dbEmptyStateStyles = `
@keyframes dbCloudFloat {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-5px); }
}
@keyframes dbPulseRing {
  0% { transform: scale(0.85); opacity: 0.5; }
  100% { transform: scale(1.5); opacity: 0; }
}
@keyframes dbFadeSlideIn {
  from { opacity: 0; transform: translateY(5px); }
  to { opacity: 1; transform: translateY(0); }
}
@keyframes dbFlowDown {
  0% { top: 0%; opacity: 0; }
  10% { opacity: 1; }
  90% { opacity: 1; }
  100% { top: calc(100% - 6px); opacity: 0; }
}
@keyframes dbRowShimmer {
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
}
`;

if (typeof document !== 'undefined') {
  const styleId = 'db-empty-state-animations';
  if (!document.getElementById(styleId)) {
    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = dbEmptyStateStyles;
    document.head.appendChild(style);
  }
}

// ============================================
// Cloud with data flow
// ============================================

const CloudWithFlow = () => (
  <div className="relative flex flex-col items-center">
    {/* Cloud icon with float */}
    <div className="relative w-36 h-36 flex items-center justify-center">
    <div
      className="relative"
      style={{ animation: 'dbCloudFloat 4s ease-in-out infinite' }}
    >
      {/* Pulse rings behind cloud */}
      <div
        className="absolute inset-0 rounded-full border border-cyan-400/20 dark:border-cyan-400/10"
        style={{ animation: 'dbPulseRing 3s ease-out infinite' }}
      />
      <div
        className="absolute inset-0 rounded-full border border-purple-400/15 dark:border-purple-400/8"
        style={{ animation: 'dbPulseRing 3s ease-out 1s infinite' }}
      />
      <div
        className="absolute inset-0 rounded-full border border-blue-400/15 dark:border-blue-400/8"
        style={{ animation: 'dbPulseRing 3s ease-out 2s infinite' }}
      />

      <svg className="w-24 h-24" viewBox="-1 0 26 24" fill="none">
        <defs>
          <linearGradient id="dbCloudGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#3b82f6" />
            <stop offset="50%" stopColor="#06b6d4" />
            <stop offset="100%" stopColor="#a855f7" />
          </linearGradient>
        </defs>
        <path
          d="M19.35 10.04A7.49 7.49 0 0012 4C9.11 4 6.6 5.64 5.35 8.04A5.994 5.994 0 000 14c0 3.31 2.69 6 6 6h13c2.76 0 5-2.24 5-5 0-2.64-2.05-4.78-4.65-4.96z"
          fill="url(#dbCloudGrad)"
          opacity="0.15"
        />
        <path
          d="M19.35 10.04A7.49 7.49 0 0012 4C9.11 4 6.6 5.64 5.35 8.04A5.994 5.994 0 000 14c0 3.31 2.69 6 6 6h13c2.76 0 5-2.24 5-5 0-2.64-2.05-4.78-4.65-4.96z"
          fill="none"
          stroke="url(#dbCloudGrad)"
          strokeWidth="0.6"
          opacity="0.7"
        />
      </svg>

      {/* Logo centered on cloud */}
      <img
        src="/logo-256.png"
        alt="Panda Extract"
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 drop-shadow-sm"
      />
    </div>
    </div>

    {/* Flow connector: vertical line with animated dot */}
    <div className="relative h-8 w-[1.5px] my-1">
      <div className="absolute inset-0 bg-gradient-to-b from-cyan-300/60 to-blue-400/60 dark:from-cyan-500/40 dark:to-blue-500/40 rounded-full" />
      <div
        className="absolute left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full bg-cyan-400 shadow-sm shadow-cyan-400/50"
        style={{ animation: 'dbFlowDown 2s ease-in-out infinite' }}
      />
    </div>
    <div className="w-0 h-0
      border-l-[3px] border-l-transparent
      border-r-[3px] border-r-transparent
      border-t-[5px] border-t-cyan-400/50 dark:border-t-cyan-400/40"
    />
  </div>
);

// ============================================
// Animated Table Preview
// ============================================

const TablePreview = () => (
  <div className="w-full max-w-[260px] mx-auto">
    <div className="rounded-xl border border-gray-200 dark:border-gray-700/50 bg-white dark:bg-gray-900/70 overflow-hidden shadow-lg">
      {/* Table header row */}
      <div className="flex items-center gap-4 px-3 py-2 border-b border-gray-100 dark:border-gray-700/30 bg-gray-50/80 dark:bg-gray-800/40">
        <div className="h-1.5 w-16 rounded-full bg-gray-300 dark:bg-gray-600" />
        <div className="h-1.5 w-12 rounded-full bg-gray-300 dark:bg-gray-600" />
        <div className="h-1.5 w-10 rounded-full bg-gray-300 dark:bg-gray-600" />
        <div className="ml-auto h-1.5 w-8 rounded-full bg-gray-300 dark:bg-gray-600" />
      </div>

      <div className="divide-y divide-gray-50 dark:divide-gray-700/20">
        {/* Row 1 */}
        <div
          className="flex items-center gap-3 px-3 py-2.5"
          style={{ animation: 'dbFadeSlideIn 0.5s ease-out both' }}
        >
          <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 flex-shrink-0" />
          <div className="flex-1 min-w-0 flex gap-3">
            <div className="h-1.5 w-1/3 rounded-full bg-gray-200 dark:bg-gray-700/60" />
            <div className="h-1.5 w-1/4 rounded-full bg-gray-200 dark:bg-gray-700/60" />
          </div>
          <span className="text-[7px] font-semibold text-emerald-500 tabular-nums flex-shrink-0">1,247</span>
        </div>

        {/* Row 2 */}
        <div
          className="flex items-center gap-3 px-3 py-2.5"
          style={{ animation: 'dbFadeSlideIn 0.5s ease-out 0.12s both' }}
        >
          <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 flex-shrink-0" />
          <div className="flex-1 min-w-0 flex gap-3">
            <div className="h-1.5 w-2/5 rounded-full bg-gray-200 dark:bg-gray-700/60" />
            <div className="h-1.5 w-1/5 rounded-full bg-gray-200 dark:bg-gray-700/60" />
          </div>
          <span className="text-[7px] font-semibold text-emerald-500 tabular-nums flex-shrink-0">856</span>
        </div>

        {/* Row 3 - active/running */}
        <div
          className="flex items-center gap-3 px-3 py-2.5"
          style={{ animation: 'dbFadeSlideIn 0.5s ease-out 0.24s both' }}
        >
          <div className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse flex-shrink-0" />
          <div className="flex-1 min-w-0 flex gap-3">
            <div className="h-1.5 w-1/4 rounded-full bg-gray-200 dark:bg-gray-700/60" />
            <div className="h-1.5 w-1/3 rounded-full bg-gray-200 dark:bg-gray-700/60" />
          </div>
          <span className="text-[7px] font-semibold text-amber-500 flex-shrink-0">312</span>
        </div>

        {/* Row 4 - faded placeholder */}
        <div
          className="flex items-center gap-3 px-3 py-2.5 opacity-30"
          style={{ animation: 'dbFadeSlideIn 0.5s ease-out 0.36s both' }}
        >
          <div className="w-1.5 h-1.5 rounded-full bg-gray-300 dark:bg-gray-600 flex-shrink-0" />
          <div className="flex-1 min-w-0 flex gap-3">
            <div className="h-1.5 w-1/3 rounded-full bg-gray-200 dark:bg-gray-700" />
            <div className="h-1.5 w-1/6 rounded-full bg-gray-200 dark:bg-gray-700" />
          </div>
        </div>
      </div>
    </div>
  </div>
);

// ============================================
// Feature Pills
// ============================================

const features = [
  {
    icon: (
      <svg className="w-3.5 h-3.5 text-blue-500 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4" />
      </svg>
    ),
    title: 'Auto-organized',
  },
  {
    icon: (
      <svg className="w-3.5 h-3.5 text-cyan-500 dark:text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    title: 'Run history',
  },
  {
    icon: (
      <svg className="w-3.5 h-3.5 text-purple-500 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
      </svg>
    ),
    title: 'Export anytime',
  },
];

// ============================================
// Main EmptyState Component
// ============================================

const EmptyState = ({ onNavigate }) => (
  <div className="flex flex-col items-center h-full px-4 sm:px-6 lg:px-8 py-8 sm:py-12 max-w-2xl mx-auto">
    {/* Header */}
    <div className="text-center mb-8 lg:mb-10">
      <h2 className="text-2xl sm:text-3xl font-black tracking-tight mb-2">
        <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-400 via-cyan-400 to-purple-400">
          Your Data Dashboard
        </span>
      </h2>
      <p className="text-gray-500 dark:text-gray-400 text-sm sm:text-base max-w-md mx-auto">
        Data from your cloud automations will appear here, organized and ready to explore
      </p>
    </div>

    {/* Illustration: Cloud flowing into table */}
    <div className="relative w-full mb-8 lg:mb-10">
      {/* Ambient background glow */}
      <div className="absolute left-1/2 -translate-x-1/2 top-1/3 w-64 h-64 bg-cyan-500/5 dark:bg-cyan-500/8 blur-3xl rounded-full pointer-events-none" />

      <div className="relative flex flex-col items-center">
        <CloudWithFlow />
        <div className="mt-2">
          <TablePreview />
        </div>
      </div>
    </div>

    {/* Feature pills */}
    <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-3 mb-8">
      {features.map((feature) => (
        <div
          key={feature.title}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/60 dark:bg-gray-800/40 border border-gray-200/50 dark:border-gray-700/30 text-xs text-gray-600 dark:text-gray-400"
        >
          {feature.icon}
          <span className="font-medium">{feature.title}</span>
        </div>
      ))}
    </div>

    {/* CTA button */}
    {onNavigate && (
      <button
        onClick={onNavigate}
        className="
          inline-flex items-center gap-2.5 px-6 py-3 text-sm font-bold
          bg-gradient-to-r from-blue-500 via-cyan-500 to-purple-500
          hover:from-blue-400 hover:via-cyan-400 hover:to-purple-400
          text-white rounded-xl
          shadow-lg shadow-cyan-500/20
          hover:shadow-xl hover:shadow-cyan-500/30
          transition-all duration-300
          hover:scale-[1.02] active:scale-[0.98]
        "
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
        Create an Automation
      </button>
    )}
  </div>
);

export default EmptyState;
